import { useEffect, useRef } from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { readAuthRedirectParams } from "@lib/auth/auth-redirect-params";
import {
  clearPasswordRecoverySession,
  isPasswordRecoveryPending,
  markPasswordRecoveryPending,
} from "@lib/auth/password-recovery";
import { createClient } from "@lib/supabase/client";
import { getSupabaseAuthStorageKey } from "@lib/supabase/auth-storage";
import { getPlatformStorage } from "@lib/platform/storage";
import { useKomutStore } from "@lib/store";
import { isMobileDevice, isOnline } from "@utils/device";
import { isRefreshTokenError, isNetworkFailure } from "@utils/auth-errors";
import { fetchProfileData } from "@utils/profile";
import {
  isAuthPath,
  isProtectedPath,
  ROUTES,
} from "@utils/navigation/routes";
import { Capacitor } from "@capacitor/core";
import {
  configurePurchases,
  logInUser,
  logOutUser,
} from "@/lib/purchases/revenuecat";

/** Reads persisted session from storage (localStorage on web, Preferences on native). */
async function readCachedSession(): Promise<Session | null> {
  if (typeof window === "undefined") return null;

  const raw = await getPlatformStorage().get(getSupabaseAuthStorageKey());
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as
      | Session
      | { currentSession?: Session | null };

    if ("user" in parsed && parsed.user?.id) return parsed as Session;

    const nested = "currentSession" in parsed ? parsed.currentSession : null;
    return nested?.user?.id ? nested : null;
  } catch {
    return null;
  }
}

async function getSessionWithTimeout(
  supabase: SupabaseClient,
  timeoutMs: number,
): Promise<Awaited<ReturnType<typeof supabase.auth.getSession>>> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error("Session check timeout")),
        timeoutMs,
      );
    });

    return (await Promise.race([sessionPromise, timeoutPromise])) as Awaited<
      ReturnType<typeof supabase.auth.getSession>
    >;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Syncs Supabase session with Zustand + handles route protection (replaces Next proxy.ts).
 */
export function useAuthGuard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const setUserInfo = useKomutStore((s) => s.setUserInfo);
  const clearUserInfo = useKomutStore((s) => s.clearUserInfo);
  const setProfileData = useKomutStore((s) => s.setProfileData);
  const setClientSessionResolved = useKomutStore(
    (s) => s.setClientSessionResolved,
  );
  const userInfo = useKomutStore((s) => s.userInfo);
  const clientSessionResolved = useKomutStore((s) => s.clientSessionResolved);

  const clearAuthAndProfileCache = () => {
    clearUserInfo();
    queryClient.clear();
  };

  const currentUserIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const cleanupRefs = useRef<{
    idleCallbackId: number | null;
    timeoutId: ReturnType<typeof setTimeout> | null;
    visibilityDebounceId: ReturnType<typeof setTimeout> | null;
  }>({
    idleCallbackId: null,
    timeoutId: null,
    visibilityDebounceId: null,
  });

  useEffect(() => {
    const supabase = createClient();
    isMountedRef.current = true;

    if (Capacitor.isNativePlatform()) {
      void configurePurchases();
    }

    const syncUserAndProfile = async (user: User) => {
      const userId = user.id;
      if (!userId) return;

      const currentUserInfo = useKomutStore.getState().userInfo;
      if (currentUserInfo?.id === userId) return;

      if (currentUserIdRef.current && currentUserIdRef.current !== userId) {
        return;
      }

      currentUserIdRef.current = userId;
      setProfileData(null);
      setUserInfo(user);

      const timeout = isMobileDevice() ? 3000 : 5000;
      const capturedUserId = userId;

      const fetchProfile = () => {
        if (
          currentUserIdRef.current !== capturedUserId ||
          !isMountedRef.current
        ) {
          return;
        }

        fetchProfileData(timeout)
          .then((profileData) => {
            if (
              currentUserIdRef.current !== capturedUserId ||
              !isMountedRef.current
            ) {
              return;
            }
            // Only a real row is stored. `fetchProfileData` returns null for a
            // missing row / failure, so the store never holds a placeholder that
            // could read as "onboarding not done".
            if (profileData) setProfileData(profileData);
          })
          .catch(() => undefined);
      };

      if ("requestIdleCallback" in window) {
        cleanupRefs.current.idleCallbackId = window.requestIdleCallback(
          fetchProfile,
          { timeout: 200 },
        );
      } else {
        cleanupRefs.current.timeoutId = setTimeout(fetchProfile, 100);
      }
    };

    const applySession = async (session: Session | null | undefined) => {
      if (!session?.user || !isMountedRef.current) return;

      // Incomplete password recovery is not a logged-in session.
      if (isPasswordRecoveryPending()) return;

      // Covers cold starts (`INITIAL_SESSION`) and cached offline sessions,
      // which do not emit SIGNED_IN. Purchases must always carry our UUID.
      if (Capacitor.isNativePlatform()) {
        void configurePurchases().then(() => logInUser(session.user.id));
      }

      await syncUserAndProfile(session.user);
      if (session.access_token) {
        supabase.realtime.setAuth(session.access_token);
      }
    };

    const checkInitialSession = async () => {
      try {
        const onResetPage = window.location.pathname.startsWith(
          ROUTES.AUTH.RESET_PASSWORD,
        );

        if (isPasswordRecoveryPending() && !onResetPage) {
          await clearPasswordRecoverySession();
          if (useKomutStore.getState().userInfo) clearAuthAndProfileCache();
          return;
        }

        if (!isOnline()) {
          const cached = await readCachedSession();
          if (isPasswordRecoveryPending() && !onResetPage) {
            return;
          }
          if (
            onResetPage &&
            cached &&
            (readAuthRedirectParams().code || isPasswordRecoveryPending())
          ) {
            markPasswordRecoveryPending();
            return;
          }
          await applySession(cached);
          return;
        }

        const timeout = isMobileDevice() ? 3000 : 5000;
        let session: Session | null = null;
        let error: unknown = null;

        try {
          const result = await getSessionWithTimeout(supabase, timeout);
          session = result.data.session;
          error = result.error;
        } catch (sessionError) {
          error = sessionError;
        }

        if (!isMountedRef.current) return;

        if (session) {
          const { code } = readAuthRedirectParams();
          if (onResetPage && (code || isPasswordRecoveryPending())) {
            markPasswordRecoveryPending();
            return;
          }

          await applySession(session);
          return;
        }

        if (error) {
          if (isRefreshTokenError(error)) {
            if (useKomutStore.getState().userInfo) clearAuthAndProfileCache();
            return;
          }

          if (!isNetworkFailure(error)) {
            console.error("[AuthGuard] Session check failed:", error);
            return;
          }

          await applySession(await readCachedSession());
          return;
        }

        if (useKomutStore.getState().userInfo) {
          clearAuthAndProfileCache();
        }
      } finally {
        if (isMountedRef.current) {
          setClientSessionResolved(true);
        }
      }
    };

    void checkInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMountedRef.current) return;

      if (event === "PASSWORD_RECOVERY") {
        markPasswordRecoveryPending();
        return;
      }

      if (event === "SIGNED_OUT") {
        if (Capacitor.isNativePlatform()) {
          void logOutUser();
        }
        const cached = await readCachedSession();
        if (!isOnline() && cached) {
          await applySession(cached);
          return;
        }

        currentUserIdRef.current = null;
        clearAuthAndProfileCache();
        return;
      }

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        if (isPasswordRecoveryPending()) return;

        if (session?.user) {
          if (Capacitor.isNativePlatform()) {
            void logInUser(session.user.id);
          }
          await syncUserAndProfile(session.user);
          if (session.access_token) {
            supabase.realtime.setAuth(session.access_token);
          }
        }
      }
    });

    return () => {
      isMountedRef.current = false;
      currentUserIdRef.current = null;
      subscription.unsubscribe();
      if (cleanupRefs.current.timeoutId) clearTimeout(cleanupRefs.current.timeoutId);
      if (
        cleanupRefs.current.idleCallbackId !== null &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(cleanupRefs.current.idleCallbackId);
      }
    };
  }, [
    queryClient,
    setUserInfo,
    clearUserInfo,
    setProfileData,
    setClientSessionResolved,
  ]);

  /** Client-side route protection (replaces Next proxy redirects). */
  useEffect(() => {
    if (!clientSessionResolved) return;

    const isAuthenticated = Boolean(userInfo);
    const search = location.search;
    const { code, error } = readAuthRedirectParams();

    if (
      isPasswordRecoveryPending() &&
      !pathname.startsWith(ROUTES.AUTH.RESET_PASSWORD)
    ) {
      void clearPasswordRecoverySession().then(() => {
        if (useKomutStore.getState().userInfo) clearAuthAndProfileCache();
      });
    }

    // Supabase may redirect to Site URL (`/`) instead of `/auth/reset-password`.
    if (
      code &&
      pathname !== ROUTES.AUTH.RESET_PASSWORD &&
      pathname !== ROUTES.AUTH.CALLBACK
    ) {
      navigate(`${ROUTES.AUTH.RESET_PASSWORD}${search}`, { replace: true });
      return;
    }

    if (error && pathname !== ROUTES.AUTH.RESET_PASSWORD) {
      navigate(`${ROUTES.AUTH.RESET_PASSWORD}${search}`, { replace: true });
      return;
    }

    if (isAuthenticated && pathname === ROUTES.SEARCH) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }

    if (!isAuthenticated && isProtectedPath(pathname)) {
      const returnUrl = `${pathname}${search}`;
      navigate(
        `${ROUTES.AUTH.SIGNIN}?returnUrl=${encodeURIComponent(returnUrl)}`,
        { replace: true },
      );
      return;
    }

    if (
      isAuthenticated &&
      isAuthPath(pathname) &&
      pathname !== ROUTES.AUTH.CALLBACK &&
      pathname !== ROUTES.AUTH.RESET_PASSWORD
    ) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }

    if (isAuthenticated && (pathname === ROUTES.LANDING || pathname === "")) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [clientSessionResolved, userInfo, pathname, location.search, navigate]);
}
